import { useCallback, useMemo } from 'react';

import { orderBy } from 'lodash-es';

import { IRestaurant } from '@rbi-ctg/store';

import { useStoreAvailabilityFns } from './use-store-availability-fns';

type SortOrderType = 'asc' | 'desc';

/**
 * Custom hook that provides a function to sort restaurants based on their availability for mobile ordering.
 * @returns A memoized function that sorts an array of restaurants by availability, either in ascending ('asc') or descending ('desc') order.
 */
export const useSortStores = () => {
  const { isStoreAvailableForPickup } = useStoreAvailabilityFns();

  /**
   * Sorts restaurants by availability.
   * @param restaurants The array of restaurants to sort.
   * @param order The sorting order ('asc' or 'desc').
   */
  const sortRestaurantByAvailability = useCallback(
    (restaurants: IRestaurant[], sortOrder: SortOrderType = 'desc') =>
      orderBy(
        restaurants,
        restaurant => isStoreAvailableForPickup(restaurant, restaurant.localTime),
        [sortOrder]
      ),
    [isStoreAvailableForPickup]
  );

  return useMemo(() => sortRestaurantByAvailability, [sortRestaurantByAvailability]);
};
